import React, { ReactElement, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Plus } from 'lucide-react';
import { ClientCompany, useListClientCompanies } from '@data';
import { useStore } from '@data/storages';
import { Roles } from '@app/utils/types';
import { useOrganizationId } from '@app/utils/hooks';
import { useViewTransitionNav } from '@app/components/core/viewTransition';
import { clientPath } from '@app/constants/url';
import { pageHeaderRowClass, pageTitleClass } from '@app/constants/styles';
import { DataTable, DataTableColumnHeader } from '@ui/components/ui/data-table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/ui/tooltip';
import { Button } from '@ui/components/ui/button';
import { Loader } from '@ui';
import AddClientDialog from './AddClientDialog';

function ClientsListLoaded({ organizationId }: { organizationId: string }): ReactElement {
  const { t } = useTranslation();
  const transitionNavigate = useViewTransitionNav();
  const { data: clients, isLoading, isError } = useListClientCompanies(organizationId);
  // Creating a client is an admin's, the same as creating a product, which is what the route enforces.
  const { organization } = useStore();
  const canAddClient = organization?.role === Roles.admin;
  const [addOpen, setAddOpen] = useState(false);

  const renderProducts = (list: string[]): ReactElement => {
    if (!list.length) return <span className="text-muted-foreground">-</span>;
    const [first] = list;
    const remaining = list.length - 1;
    if (remaining <= 0) return <span>{first}</span>;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-default">
            {first} <span className="font-bold">{t('nMore', { count: remaining })}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent>{list.join(', ')}</TooltipContent>
      </Tooltip>
    );
  };

  /* eslint-disable react/no-unstable-nested-components -- TanStack Table column render props */
  const columns = useMemo<ColumnDef<ClientCompany>[]>(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }) => <DataTableColumnHeader column={column} title={t('titles.name')} />,
        cell: ({ row }) => <span className="min-w-[8rem] font-medium">{row.original.name}</span>,
      },
      {
        id: 'products',
        accessorFn: row => row.products.map(product => product.name).join(', '),
        header: ({ column }) => <DataTableColumnHeader column={column} title={t('titles.products')} />,
        cell: ({ row }) => (
          <div className="min-w-[10rem]">{renderProducts(row.original.products.map(product => product.name))}</div>
        ),
      },
      {
        id: 'status',
        accessorFn: row => (row.isActive ? t('activeClient') : t('inactiveClient')),
        header: ({ column }) => <DataTableColumnHeader column={column} title={t('status')} />,
        cell: ({ row }) => (row.original.isActive ? t('activeClient') : t('inactiveClient')),
      },
      {
        id: 'go',
        enableSorting: false,
        header: '',
        meta: { className: 'w-0 text-right' },
        cell: () => <ChevronRight className="size-4 text-muted-foreground" />,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );
  /* eslint-enable react/no-unstable-nested-components */

  return (
    <>
      <div className={`${pageHeaderRowClass} mb-5 flex flex-col gap-4 md:flex-row md:items-center md:justify-between`}>
        <h1 className={pageTitleClass}>{t('titles.clients')}</h1>
        {canAddClient && (
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="size-4" />
            {t('addClient')}
          </Button>
        )}
      </div>

      {/* Distinct from the empty state: "no clients" on a failed fetch reads as an organization with
          none, which is the opposite of what happened. */}
      {isError && <span className="text-sm text-destructive">{t('somethingWentWrong')}</span>}

      {!isError &&
        (!isLoading && !clients?.items.length ? (
          <span className="text-sm text-muted-foreground">{t('noClients')}</span>
        ) : (
          <DataTable<ClientCompany>
            columns={columns}
            data={clients?.items ?? []}
            isLoading={isLoading}
            initialSorting={[{ id: 'name', desc: false }]}
            getRowId={row => row.id}
            onRowClick={client => transitionNavigate(clientPath(organizationId, client.id), 'forward')}
            padded
          />
        ))}

      <AddClientDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        organizationId={organizationId}
        role={organization?.role}
      />
    </>
  );
}

/** Who we do the work for. A row is a company; the people at it live on its own page. */
const ClientsList = (): ReactElement => {
  const organizationId = useOrganizationId();
  if (!organizationId) {
    return <Loader />;
  }
  return <ClientsListLoaded organizationId={organizationId} />;
};

export default ClientsList;
